import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { POSITIONS, archetypesFor, normalizePosition } from "@/lib/positionTraits";

/** Reusable position dropdown bound to the canonical CSV-driven position list. */
export function PositionSelect({
  value,
  onChange,
  testId = "position-select",
  placeholder = "Select position",
  triggerClassName = "bg-white/5 border-white/10 text-1",
  disabled = false,
}) {
  const canonical = normalizePosition(value || "");
  return (
    <Select value={canonical || undefined} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testId} className={triggerClassName}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="glass-strong border-white/10 max-h-72">
        {POSITIONS.map((p) => (
          <SelectItem key={p} value={p} data-testid={`${testId}-option-${p}`}>
            {p}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Archetype dropdown that auto-populates options from the chosen position. */
export function ArchetypeSelect({
  position,
  value,
  onChange,
  testId = "archetype-select",
  placeholder = "Select archetype",
  triggerClassName = "bg-white/5 border-white/10 text-1",
  disabled = false,
}) {
  const options = archetypesFor(position);
  const isDisabled = disabled || options.length === 0;
  return (
    <Select value={value || undefined} onValueChange={onChange} disabled={isDisabled}>
      <SelectTrigger data-testid={testId} className={triggerClassName}>
        <SelectValue
          placeholder={options.length === 0 ? "No archetypes for this position" : placeholder}
        />
      </SelectTrigger>
      <SelectContent className="glass-strong border-white/10 max-h-72">
        {options.map((a) => (
          <SelectItem key={a} value={a} data-testid={`${testId}-option-${a}`}>
            {a}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Stacked Position + Archetype field group for player create / edit forms. */
export function PositionArchetypeFields({
  position,
  archetype,
  onPositionChange,
  onArchetypeChange,
  testIdPrefix = "player",
  layout = "grid", // "grid" | "stacked"
}) {
  const wrapper = layout === "grid" ? "grid grid-cols-2 gap-3" : "space-y-3";
  return (
    <div className={wrapper}>
      <div className="space-y-1">
        <Label className="text-2 text-xs">Position</Label>
        <PositionSelect
          value={position}
          onChange={(v) => {
            onPositionChange(v);
            // Clearing the archetype is the safest default: when a user changes
            // position, the previous archetype almost certainly no longer fits.
            if (archetype) onArchetypeChange("");
          }}
          testId={`${testIdPrefix}-position`}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-2 text-xs">Archetype</Label>
        <ArchetypeSelect
          position={position}
          value={archetype}
          onChange={onArchetypeChange}
          testId={`${testIdPrefix}-archetype`}
        />
      </div>
    </div>
  );
}
